import React, { useEffect, useMemo, useState } from "react";
import { ReactComponent as CalendarIcon } from "./assets/calendar.svg";
import { ReactComponent as ShieldIcon } from "./assets/shield.svg";
import classNames from "classnames";
import styles from "./InstantTournaments.module.scss";
import addTournament from "./assets/add-tournament.png";
import goldCoins from "./assets/gold-coins.svg";
import coins from "./assets/coins.png";
import players from "./assets/players.png";
import nock from "./assets/nock.svg";
import timeIcon from "./assets/time.png";
import { AbsoluteCenter } from "../../AbsoluteCenter";
import { loader } from "graphql.macro";
import { useQuery } from "../../../hooks/useQuery";
import {
  MyMatchTournamentsQuery,
  MyMatchTournamentsQueryVariables,
} from "./graphql/MyTournamentsQuery.generated";
import { useTranslation } from "react-i18next";
import { Loader } from "../../Loader";
import {
  InstantTournament,
  MatchTournamentType,
  ScheduledTournament,
  TournamentPlayerPerTeam,
  TournamentStatus,
  TournamentType,
} from "../../../graphql/generated";
import { TournamentFilter } from "./MyTournamentFilter";
import { useLocation, useParams } from "react-router";
import { Link, useHistory } from "react-router-dom";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFlag, faRankingStar } from "@fortawesome/pro-solid-svg-icons";

const MY_TOURNAMENT_QUERY = loader("./graphql/MyTournamentsQuery.graphql");

type TournamentWithTypename =
  | (InstantTournament & { __typename: "InstantTournament" })
  | (ScheduledTournament & { __typename: "ScheduledTournament" });

const EmptyTournamentList = ({ type }: { type: MatchTournamentType }) => {
  const { t } = useTranslation();

  const currentText = useMemo(() => {
    if (type === MatchTournamentType.Instant) {
      return t("instant_tournament.no_instant_tournaments");
    } else if (type === MatchTournamentType.Scheduled) {
      return t("instant_tournament.no_scheduled_tournaments");
    } else {
      return t("instant_tournament.no_tournaments");
    }
  }, [type]);

  return (
    <div className="flex flex-col justify-between gap-6 h-full w-full">
      <div className="flex-1 rounded-b-lg overflow-hidden bg-gray-900 relative">
        <AbsoluteCenter>
          <div className="flex flex-col gap-3 text-white items-center text-xl leading-loose	">
            <img src={addTournament} alt="vip-icon" width="100px" />
            {currentText}
          </div>
        </AbsoluteCenter>
      </div>
    </div>
  );
};

const TournamentItem = ({
  myTournament,
  tournament,
  onJoinTournament,
  onViewTournament,
  gameId,
  roomId,
  scheduled,
}: {
  myTournament?: boolean;
  tournament: TournamentWithTypename;
  onJoinTournament?: (id: string) => void;
  onViewTournament?: ({ id, type }: { id: string; type: string }) => void;
  gameId?: string;
  roomId?: string;
  scheduled?: boolean;
}) => {
  const { t } = useTranslation();
  const { push } = useHistory();
  const [timeLeft, setTimeLeft] = useState<string>("");

  useEffect(() => {
    if (tournament.__typename === "ScheduledTournament") {
      const interval = setInterval(() => {
        const now = dayjs();
        const start = dayjs(tournament?.startDateTime);
        const diffSeconds = start.diff(now, "second");

        if (diffSeconds <= 0) {
          setTimeLeft("");
          clearInterval(interval);
          return;
        }

        if (diffSeconds < 3600) {
          const minutes = Math.floor(diffSeconds / 60);
          setTimeLeft(`${minutes} ${t("instant_tournament.minutes")}`);
        } else {
          setTimeLeft("");
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [tournament]);

  const getMaxValue = () => {
    const awards = tournament?.awards || [];
    const maxValue = Math.max(...awards!.map((a) => a.amount.value));

    return maxValue;
  };

  return (
    <div
      className={classNames(
        "flex",
        "gap-6",
        "p-5",
        "text-white",
        "items-center",
        "rounded-lg",
        styles.tournamentItem
      )}
    >
      <div className={styles.image}>
        <img
          src={`${process.env.REACT_APP_API_HOST}/${
            scheduled ? "scheduled-tournaments" : "instant-tournaments"
          }/${tournament.image}`}
          alt="img"
        />
      </div>
      <div className="flex flex-col gap-1 flex-grow">
        {scheduled && (
          <div className={styles.scheduledTime}>
            <div
              className="flex justify-center items-center"
              style={{
                width: "1.375rem",
                height: "1.375rem",
                borderRadius: "100%",
                background: "#DD6B20",
              }}
            >
              <CalendarIcon width={14} height={14} color="currentColor" />
            </div>
            <div>
              {tournament.__typename === "ScheduledTournament" &&
                tournament?.startDateTime &&
                dayjs(tournament?.startDateTime).format("ddd DD MMM · HH.mm")}
            </div>
          </div>
        )}
        <span className="font-semibold text-lg">{tournament.name}</span>
        {!scheduled && (
          <div className="flex gap-2 text-sm">
            <span className={styles.prize}>{t("prize")}</span>
            <img src={goldCoins} width={24} height={24} alt="coins" />
            <span className="font-semibold">{getMaxValue()}</span>
          </div>
        )}
        <div className="flex gap-3 text-sm">
          {scheduled && (
            <div className="flex items-center gap-2">
              <span className={styles.prize}>{t("prize")}</span>
              <img src={goldCoins} width={24} height={24} alt="coins" />
              <span className="font-semibold">{getMaxValue()}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <img src={coins} width={16} height={16} alt="coins" />
            <span className={styles.info}>{t("instant_tournament.entry")}</span>
            <span className={styles.info}>{tournament.price.value}</span>
          </div>
          <div className="flex items-center gap-2">
            <ShieldIcon width={14} height={14} />
            <span className={styles.info}>
              {tournament.playerPerTeam === TournamentPlayerPerTeam.OneVsOne
                ? "1x1"
                : "2x2"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <img src={players} width={16} height={16} alt="coins" />
            <span className={styles.info}>{`${t("players")} ${
              tournament.joinedPlayers
            }/${tournament.numberOfPlayers}`}</span>
          </div>
          {tournament.type === TournamentType.Private && (
            <div className="flex items-center gap-2">
              <img src={nock} width={16} height={16} alt="coins" />
              <span className={styles.info}>{t("private")}</span>
            </div>
          )}
        </div>
      </div>
      {tournament.status === TournamentStatus.Cancelled && (
        <div className="w-2/12 flex flex-col gap-2">
          <div className="flex flex-col gap-2">
            <span className={classNames("text-sm", styles.info)}>
              {t("instant_tournament.tournament_cancelled")}
            </span>
          </div>
        </div>
      )}
      {tournament.status !== TournamentStatus.Completed &&
      tournament.status !== TournamentStatus.Cancelled ? (
        <div className="w-2/12 flex flex-col gap-2">
          {scheduled && !!timeLeft ? (
            <div className="flex items-center gap-2">
              <img src={timeIcon} width={26} height={26} alt="" />
              <span className={classNames("text-sm", styles.info)}>
                {t("instant_tournament.starts_in")}
              </span>
              <span className="text-sm font-bold" style={{ color: "#FFAB28" }}>
                {timeLeft}
              </span>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <span className={classNames("text-sm", styles.info)}>
                {t("instant_tournament.tournament")}
              </span>
              <span className="text-sm text-blue-600">
                {t("instant_tournament.active_now")}
              </span>
            </div>
          )}

          {tournament.type !== TournamentType.Private && (
            <button
              className="btn-green-500 rounded-md shadow-xl text-white w-full py-1 text-sm h-7"
              onClick={() => {
                myTournament || tournament.isParticipant
                  ? push(
                      `/area/${roomId}:${gameId}/${
                        scheduled
                          ? "scheduled-tournaments/current"
                          : "instant-tournaments/current"
                      }/${tournament.id}`
                    )
                  : onJoinTournament && onJoinTournament(tournament.id);
              }}
            >
              {myTournament || tournament.isParticipant
                ? t("instant_tournament.continue")
                : t("join")}
            </button>
          )}
        </div>
      ) : tournament.isParticipant &&
        tournament.status !== TournamentStatus.Cancelled ? (
        <button
          className="btn-orange-600 rounded-md shadow-xl text-white w-2/12 py-1 text-sm h-7"
          onClick={() => {
            push(
              `/area/${roomId}:${gameId}/${
                scheduled
                  ? "scheduled-tournaments/current"
                  : "instant-tournaments/current"
              }/${tournament?.id}?type=${
                tournament.__typename === "InstantTournament"
                  ? "instant-tournament"
                  : "scheduled-tournament"
              }`
            );
          }}
        >
          {t("instant_tournament.view_bracket")}
        </button>
      ) : (
        <></>
      )}
    </div>
  );
};

export const MyTournaments = () => {
  const { sectionId } = useParams<{
    sectionId: string;
  }>();
  const { t } = useTranslation();
  const [tournamentList, setTournamentList] = useState<
    MyMatchTournamentsQuery | undefined
  >(undefined);
  const [viewTournament, setViewTournament] = useState<{
    id?: string;
    type?: string;
  }>({});
  const [filterStatus, setFilterStatus] = useState<TournamentStatus[]>([]);
  const [playersPerTeam, setPlayersPerTeam] = useState<
    TournamentPlayerPerTeam[]
  >([]);
  const [tournamentType, setTournamentType] = useState<MatchTournamentType[]>(
    []
  );

  const filterData = React.useMemo(() => {
    return {
      roomId: sectionId?.split(":")[0],
      gameId: sectionId?.split(":")[1],
    };
  }, [sectionId]);

  const [{ data, fetching }, reexecuteQuery] = useQuery<
    MyMatchTournamentsQuery,
    MyMatchTournamentsQueryVariables
  >({
    query: MY_TOURNAMENT_QUERY,
    pause: false,
    variables: {
      filter: {
        roomId: filterData.roomId,
        gameId: filterData.gameId,
        matchTournamentTypes: tournamentType,
        statuses: filterStatus,
        playersPerTeam: playersPerTeam,
      },
    },
  });

  useEffect(() => {
    setTournamentList(data);
  }, [data]);

  const fetchData = () => {
    reexecuteQuery({ requestPolicy: "network-only" });
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchData();
  }, [filterStatus]);

  useEffect(() => {
    fetchData();
  }, [playersPerTeam]);

  useEffect(() => {
    fetchData();
  }, [tournamentType]);

  return (
    <div className="h-full">
      <div className="h-full flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div
            className={classNames(
              "flex",
              "gap-3",
              "p-1",
              "rounded-md",
              styles.tabList
            )}
          >
            <Link
              to={`/area/${filterData?.roomId}:${filterData.gameId}/instant-tournaments`}
              className={classNames(
                "flex",
                "gap-3",
                "items-center",
                "py-1",
                "px-4",
                "rounded-md",
                styles.tab
              )}
            >
              <div>
                <FontAwesomeIcon icon={faRankingStar} />
              </div>
              {t("instant_tournament.instant")}
            </Link>
            <Link
              to={`/area/${filterData?.roomId}:${filterData.gameId}/scheduled-tournaments`}
              className={classNames(
                "flex",
                "gap-3",
                "items-center",
                "py-1",
                "px-4",
                "rounded-md",
                styles.tab
              )}
            >
              <div className="flex items-center justify-between w-6 h-6">
                <CalendarIcon width={24} height={24} color="currentColor" />
              </div>
              {t("instant_tournament.scheduled")}
            </Link>
            <Link
              to={`/area/${filterData?.roomId}:${filterData.gameId}/my-tournaments`}
              className={classNames(
                "flex",
                "gap-3",
                "items-center",
                "py-1",
                "px-4",
                "rounded-md",
                styles.tab,
                styles.active
              )}
            >
              <div>
                <FontAwesomeIcon icon={faFlag} />
              </div>
              {t("instant_tournament.my_tournaments")}
            </Link>
          </div>
        </div>
        <div>
          <span className="text-white font-bold block">
            {t("instant_tournament.my_tournaments")}
          </span>
        </div>
        {fetching ? (
          <AbsoluteCenter>
            <Loader description={t("loading_tournament")} />
          </AbsoluteCenter>
        ) : (
          <div className="flex gap-2" style={{ height: "calc(100% - 90px)" }}>
            <div
              className={classNames(
                "flex",
                "flex-col",
                "gap-2",
                "rounded-lg",
                "bg-gray-900",
                "p-1",
                "overflow-auto",
                styles.scrollable,
                styles.filter
              )}
            >
              <TournamentFilter
                onChangeStatus={(value) => {
                  setFilterStatus(value);
                }}
                onChangeFormat={(value) => {
                  setPlayersPerTeam(value);
                }}
                onChangeType={(value) => {
                  setTournamentType(value);
                }}
                currentStatus={filterStatus}
                currentFormat={playersPerTeam}
                tournamentType={tournamentType}
              />
            </div>
            {!!tournamentList?.myMatchTournaments?.length ? (
              <div
                className={classNames(
                  "flex",
                  "flex-col",
                  "flex-1",
                  "gap-2",
                  "rounded-lg",
                  "bg-gray-900",
                  "p-1",
                  "overflow-auto",
                  "w-full",
                  styles.scrollable
                )}
              >
                {tournamentList?.myMatchTournaments?.map((tournament) => (
                  <TournamentItem
                    key={tournament.id}
                    gameId={filterData.gameId}
                    roomId={filterData.roomId}
                    myTournament
                    tournament={tournament as TournamentWithTypename}
                    onViewTournament={setViewTournament}
                    scheduled={tournament.__typename === "ScheduledTournament"}
                  />
                ))}
              </div>
            ) : (
              <EmptyTournamentList type={tournamentType[0]} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
